/**
 * The generic record form: who/what (subjects), when (with honest precision), and the type's fields.
 * Used for "Add a record", for correcting history, and for editing a proposal item. A module's
 * custom Form replaces it for its event type.
 */
import { useState, useSyncExternalStore, type ReactNode } from "react";
import { localDate, type EntityKind, type EventDTO, type EventInput, type ObservedTime, type Precision, type SubjectRef } from "../../contract";
import { ApiError, api } from "../lib/api";
import { t } from "../lib/i18n";
import { invalidateAll } from "../lib/query";
import { useMe } from "../lib/session";
import { eventView, registry } from "../registry";
import { Button, Field, Icon, Select, Sheet, TextInput, toast } from "../ui/kit";
import { RefPicker } from "./pickers";
import { SchemaForm, issuesToErrors, type Errors, type Values } from "./SchemaForm";
import type { EventFormProps } from "../module";
import { ENTITY_FIELDS, ENTITY_LABELS } from "../../contract";

const PRECISION_LABELS: Record<Precision, string> = {
  minute: "Exact time", date: "On this date", estimated_date: "Around this date", date_range: "Between dates", month: "Sometime that month", year: "Sometime that year", unknown: "Not known",
};

export function WhenInput({ value, onChange, error }: { value: ObservedTime; onChange: (v: ObservedTime) => void; error?: string }) {
  const p = value.precision;
  return (
    <Field label={t("When")} error={error}>
      <div className="when">
        <Select aria-label={t("How exact")} value={p} onChange={(e) => {
          const next = e.target.value as Precision;
          const base = (value.value ?? new Date().toISOString()).slice(0, 10);
          onChange(next === "unknown" ? { precision: next } : next === "minute" ? { precision: next, value: `${base}T${new Date().toTimeString().slice(0, 5)}` }
            : next === "month" ? { precision: next, value: base.slice(0, 7) } : next === "year" ? { precision: next, value: base.slice(0, 4) }
            : next === "date_range" ? { precision: next, value: base, end: value.end ?? base } : { precision: next, value: base });
        }}>
          {(Object.keys(PRECISION_LABELS) as Precision[]).map((k) => <option key={k} value={k}>{t(PRECISION_LABELS[k])}</option>)}
        </Select>
        {p === "minute" && <TextInput type="datetime-local" aria-label={t("Date and time")} value={value.value?.slice(0, 16) ?? ""} onChange={(e) => onChange({ ...value, value: e.target.value })} />}
        {(p === "date" || p === "estimated_date" || p === "date_range") && <TextInput type="date" aria-label={t("Date")} value={value.value?.slice(0, 10) ?? ""} onChange={(e) => onChange({ ...value, value: e.target.value })} />}
        {p === "date_range" && <TextInput type="date" aria-label={t("End date")} value={value.end ?? ""} onChange={(e) => onChange({ ...value, end: e.target.value })} />}
        {p === "month" && <TextInput type="month" aria-label={t("Month")} value={value.value ?? ""} onChange={(e) => onChange({ ...value, value: e.target.value })} />}
        {p === "year" && <TextInput type="number" aria-label={t("Year")} value={value.value ?? ""} onChange={(e) => onChange({ ...value, value: e.target.value })} />}
      </div>
    </Field>
  );
}

export function SubjectsInput({ type, value, onChange, errors, labels }: { type: string; value: SubjectRef[]; onChange: (v: SubjectRef[]) => void; errors: Errors; labels?: Record<string, string> }) {
  const def = registry.event(type);
  if (!def) return null;
  return (
    <>
      {def.subjects.map((spec) => {
        const ids = value.filter((s) => s.role === spec.role).map((s) => s.id);
        const set = (next: string | string[] | null) => {
          const list = (Array.isArray(next) ? next : next ? [next] : []).map((id) => ({ kind: spec.kind, id, role: spec.role }));
          onChange([...value.filter((s) => s.role !== spec.role), ...list]);
        };
        if (!spec.required && !ids.length && def.subjects.length > 2 && spec !== def.subjects[0]) return null;
        return (
          <Field key={spec.role} label={t(spec.label)} error={errors[`subjects.${spec.role}`]} optional={!spec.required} hint={spec.help ? t(spec.help) : undefined}>
            <RefPicker target={spec.kind} multiple={spec.many} value={spec.many ? ids : ids[0]} onChange={set} labels={labels} />
          </Field>
        );
      })}
    </>
  );
}

export function GenericEventForm({ initial, existing, onSubmit, onCancel }: EventFormProps) {
  const { data: me } = useMe();
  const def = registry.event(initial.type)!;
  const today = me?.ranch ? localDate(new Date(), me.ranch.timezone) : new Date().toISOString().slice(0, 10);
  const [subjects, setSubjects] = useState<SubjectRef[]>(initial.subjects ?? []);
  const [occurred, setOccurred] = useState<ObservedTime>(initial.occurred ?? { value: today, precision: "date" });
  const [data, setData] = useState<Values>(initial.data ?? {});
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const labels = existing?.labels;
  const submit = async () => {
    setBusy(true);
    setErrors({});
    try {
      await onSubmit({ type: def.type, subjects, occurred, data, place_id: initial.place_id ?? null, job_id: initial.job_id ?? null, batch_id: initial.batch_id ?? null }, reason || undefined);
    } catch (err) {
      if (err instanceof ApiError && err.issues.length) setErrors(issuesToErrors(err.issues));
      else toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="event-form" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <SubjectsInput type={def.type} value={subjects} onChange={setSubjects} errors={errors} labels={labels} />
      <WhenInput value={occurred} onChange={setOccurred} error={errors.occurred} />
      <SchemaForm fields={def.fields} value={data} onChange={setData} errors={errors} labels={labels} />
      {existing && <Field label={t("Why the change?")} optional><TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("Wrong date, wrong animal…")} /></Field>}
      <div className="form-actions">
        <Button variant="primary" size="lg" type="submit" busy={busy} icon="check">{existing ? t("Save correction") : t("Save")}</Button>
        <Button variant="ghost" onClick={onCancel}>{t("Cancel")}</Button>
      </div>
    </form>
  );
}

export function EventFormFor(props: EventFormProps) {
  const Custom = eventView(props.initial.type)?.Form;
  return Custom ? <Custom {...props} /> : <GenericEventForm {...props} />;
}

// ---------------------------------------------------------------- the global record sheet

interface RecordRequest { initial: Partial<EventInput> & { type: string }; existing?: EventDTO; onDone?: (e: EventDTO) => void }
interface EntityRequest { kind: EntityKind; id?: string; initial?: Values; onDone?: (id: string) => void }
let current: { record?: RecordRequest; entity?: EntityRequest } = {};
const listeners = new Set<() => void>();
const set = (next: typeof current) => { current = next; listeners.forEach((l) => l()); };

/** Open the record form anywhere: openRecord({ initial: { type: "health.treatment", subjects: [...] } }). */
export function openRecord(req: RecordRequest): void { set({ record: req }); }
/** Open the create/edit form for an animal, place, contact, product... */
export function openEntity(req: EntityRequest): void { set({ entity: req }); }

export function RecordSheetHost() {
  const state = useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => current);
  const close = () => set({});
  const rec = state.record;
  const ent = state.entity;
  let body: ReactNode = null;
  let title: ReactNode = null;
  if (rec) {
    const def = registry.event(rec.initial.type);
    title = def ? <span className="sheet-title-icon"><Icon name={def.icon} size={20} />{rec.existing ? t("Correct {{record}}", { record: t(def.label).toLowerCase() }) : t(def.label)}</span> : null;
    body = def && (
      <EventFormFor initial={rec.initial} existing={rec.existing} onCancel={close} onSubmit={async (input, reason) => {
        const saved = rec.existing
          ? await api.post<EventDTO>(`/events/${rec.existing.id}/correct`, { ...input, reason })
          : (await api.post<{ items: EventDTO[] }>("/events", input)).items[0];
        await invalidateAll();
        toast(rec.existing ? t("Correction saved") : t("Saved"), { tone: "good" });
        close();
        rec.onDone?.(saved);
      }} />
    );
  }
  if (ent) {
    title = ent.id ? t("Edit {{thing}}", { thing: t(ENTITY_LABELS[ent.kind]).toLowerCase() }) : t("New {{thing}}", { thing: t(ENTITY_LABELS[ent.kind]).toLowerCase() });
    body = <EntityForm req={ent} onClose={close} />;
  }
  return <Sheet open={!!(rec || ent)} onClose={close} title={title} size="md">{body}</Sheet>;
}

const ROUTES: Record<EntityKind, string> = { animal: "animals", group: "groups", place: "places", contact: "contacts", stock_item: "stock-items", stock_lot: "stock-lots", asset: "assets", fin_doc: "fin-docs" };

function EntityForm({ req, onClose }: { req: EntityRequest; onClose: () => void }) {
  const [value, setValue] = useState<Values>(req.initial ?? {});
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const fields = ENTITY_FIELDS[req.kind];
  const submit = async () => {
    setBusy(true);
    try {
      let id = req.id;
      if (id) await api.patch(`/${ROUTES[req.kind]}/${id}`, value);
      else id = (await api.post<{ id: string }>(`/${ROUTES[req.kind]}`, value)).id;
      await invalidateAll();
      toast(t("Saved"), { tone: "good" });
      onClose();
      req.onDone?.(id!);
    } catch (err) {
      if (err instanceof ApiError && err.issues.length) setErrors(issuesToErrors(err.issues, ""));
      else toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <SchemaForm fields={req.id ? Object.fromEntries(Object.entries(fields).map(([k, f]) => [k, { ...f, required: false }])) : fields} value={value} onChange={setValue} errors={errors} />
      <div className="form-actions">
        <Button variant="primary" size="lg" type="submit" busy={busy} icon="check">{t("Save")}</Button>
        <Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button>
      </div>
    </form>
  );
}
